'use client';

import { useEffect, useState } from 'react';

import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';

import { Menu, X } from 'lucide-react';

import '../styles.css';
import { PadelDistrictHeaderAccountSection } from './header-account-section';

const LOGO_SRC = '/landing-pages/padeldistrict/logos/wordmark-en-white.svg';
const BOOK_HREF = '/book';
const SIGN_IN_HREF = '/auth/sign-in';

// Section anchors live on the landing page only. Prefix with `/` so they
// navigate back to `/` (then scroll) when clicked from a sub-route like /book.
const navLinks: Array<{ label: string; href: string }> = [
  { label: 'Book', href: BOOK_HREF },
  { label: 'Academy', href: '/#academy' },
  { label: 'Socials & Events', href: '/#events' },
  { label: 'Partners', href: '/#partners' },
  { label: 'Location', href: '/#location' },
];

function Logo({ className }: { className?: string }) {
  return (
    <img
      src={LOGO_SRC}
      alt="Padel District"
      className={className}
      onError={(e) => {
        const img = e.target as HTMLImageElement;
        img.style.display = 'none';
        const span = document.createElement('span');
        span.style.cssText =
          "font-family:'Barlow Condensed',sans-serif;font-size:1.1rem;font-weight:800;letter-spacing:0.04em;color:#fff;text-transform:uppercase;";
        span.textContent = 'PADEL DISTRICT';
        img.parentElement?.appendChild(span);
      }}
    />
  );
}

/**
 * Shared PadelDistrict header rendered by (tenant)/layout.tsx across every
 * sub-route (/book, /bookings, /coaches, …). On the landing page (`/`) it
 * overlays the hero transparently and lets the page scroll under it; on every
 * other route it renders solid with a spacer so page content clears the fixed
 * bar. Extracted from the landing page's inline <Navbar> so sub-routes are no
 * longer chromeless (they previously had no header at all).
 */
export function PadelDistrictHeader() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isLandingPage = pathname === '/';
  // Solid whenever we're not at the very top of the landing hero.
  const solid = !isLandingPage || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, []);

  return (
    <div className="padeldistrict-root pd-chrome">
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: solid ? 'rgba(0,53,120,0.97)' : 'transparent',
          backdropFilter: solid ? 'blur(10px)' : 'none',
          borderBottom: solid ? '1px solid rgba(255,255,255,0.08)' : 'none',
        }}
      >
        <div className="container flex items-center justify-between h-16 md:h-18">
          <Link href="/" className="flex items-center z-10 flex-shrink-0">
            <Logo className="h-7 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link key={link.label} href={link.href} className="nav-link">
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center">
            <PadelDistrictHeaderAccountSection />
          </div>

          <button
            type="button"
            className="lg:hidden text-white p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {menuOpen ? (
          <div
            className="lg:hidden absolute top-full left-0 right-0 py-6 px-6 flex flex-col gap-5"
            style={{
              backgroundColor: 'rgba(0,53,120,0.98)',
              backdropFilter: 'blur(10px)',
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="text-white/80 text-sm uppercase tracking-widest font-semibold hover:text-white transition-colors"
                style={{ fontFamily: "'DM Sans',sans-serif" }}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 flex flex-col gap-3">
              <a
                href={SIGN_IN_HREF}
                className="text-white/40 text-sm uppercase tracking-widest"
                style={{ fontFamily: "'DM Sans',sans-serif" }}
                onClick={() => setMenuOpen(false)}
              >
                Sign In
              </a>
              <Link
                href={BOOK_HREF}
                className="btn-green text-center"
                onClick={() => setMenuOpen(false)}
              >
                Book Now
              </Link>
            </div>
          </div>
        ) : null}
      </nav>

      {/* Spacer so sub-route content clears the fixed bar. The landing page
          intentionally has none — its hero sits under the transparent nav. */}
      {!isLandingPage && <div className="h-16 md:h-[72px]" />}
    </div>
  );
}
